import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Ticket, Minus, Plus, CheckCircle2, Trophy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BookingDialog = ({ event, open, onOpenChange }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const reset = () => {
    setName("");
    setEmail("");
    setQty(1);
    setConfirmation(null);
    setLoading(false);
  };

  const handleOpenChange = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event_id: event.id,
          event_name: event.artist,
          name,
          email,
          quantity: qty,
        }),
      });
      if (!res.ok) throw new Error("failed");
      const data = await res.json();
      setConfirmation(data);
      toast.success("Reservation confirmed! Check your email.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!event) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="booking-dialog"
        className="bg-[#0a0a0a] border-white/10 text-foreground max-w-md"
      >
        <AnimatePresence mode="wait">
          {confirmation ? (
            <motion.div
              key="confirm"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-6"
              data-testid="booking-confirmation"
            >
              <CheckCircle2 className="mx-auto text-secondary mb-4" size={48} />
              <h3 className="font-display text-2xl font-bold mb-2">You're in!</h3>
              <p className="text-muted-foreground mb-6">
                Your spot for <b className="text-foreground">{event.artist}</b> is
                reserved. Confirmation sent to {email}.
              </p>
              <div className="rounded-xl border border-white/10 p-4 mb-6">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  Reference
                </p>
                <p className="font-display text-2xl text-primary">
                  {confirmation.reference}
                </p>
              </div>
              <button
                data-testid="booking-close-btn"
                onClick={() => handleOpenChange(false)}
                className="w-full bg-primary text-primary-foreground rounded-full py-3 font-medium hover:bg-white hover:text-black transition-colors duration-300"
              >
                Done
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">
                  Reserve tickets
                </DialogTitle>
              </DialogHeader>
              <div className="mt-2 mb-5">
                <p className="font-display text-xl">{event.artist}</p>
                <p className="text-sm text-muted-foreground">
                  {event.date} · {event.venue}
                </p>
                {event.prize && (
                  <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold uppercase tracking-[0.15em] bg-secondary/15 text-secondary px-3 py-1.5 rounded-full">
                    <Trophy size={13} /> Grand prize: {event.prize}
                  </span>
                )}
              </div>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="b-name" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                    Full name
                  </Label>
                  <Input
                    id="b-name"
                    data-testid="booking-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mt-1.5 bg-white/5 border-white/10"
                    placeholder="Jane Doe"
                  />
                </div>
                <div>
                  <Label htmlFor="b-email" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                    Email
                  </Label>
                  <Input
                    id="b-email"
                    data-testid="booking-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1.5 bg-white/5 border-white/10"
                    placeholder="you@email.com"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                    Tickets
                  </Label>
                  <div className="mt-1.5 flex items-center gap-4">
                    <button
                      type="button"
                      data-testid="booking-qty-minus"
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                      className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:border-primary transition-colors"
                    >
                      <Minus size={16} />
                    </button>
                    <span data-testid="booking-qty" className="font-display text-2xl w-8 text-center">
                      {qty}
                    </span>
                    <button
                      type="button"
                      data-testid="booking-qty-plus"
                      onClick={() => setQty((q) => Math.min(10, q + 1))}
                      className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:border-primary transition-colors"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
                <button
                  type="submit"
                  data-testid="booking-submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-full py-3.5 font-medium hover:bg-white hover:text-black transition-colors duration-300 disabled:opacity-60"
                >
                  <Ticket size={18} />
                  {loading ? "Reserving..." : "Confirm reservation"}
                </button>
                <p className="text-xs text-center text-muted-foreground">
                  Free reservation — pay at the door. You'll get a confirmation email.
                </p>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};
